/**
 * Single source of truth for the lodge's contact phone number.
 *
 * Mobile dialers only reliably hand off a `tel:` link to the phone app when the
 * href is an unformatted E.164 number (`+1` + 10 digits, no spaces, dashes, or
 * parentheses). Display text can stay human-readable.
 */

/** Human-readable phone number shown in the UI. */
export const LODGE_PHONE_DISPLAY = "(907) 312-1019";

/** E.164 form used in `tel:` hrefs and structured data. */
export const LODGE_PHONE_E164 = "+19073121019";

/** Schema.org / hCard friendly form. */
export const LODGE_PHONE_SCHEMA = "+1-907-312-1019";

/** Ready-to-use href for the lodge phone number. */
export const LODGE_PHONE_HREF = `tel:${LODGE_PHONE_E164}`;

/**
 * Build an E.164 `tel:` href from any formatted US phone number.
 * Falls back to the raw digits for non-US-looking input.
 */
export function telHref(input: string): string {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `tel:+${digits}`;
  return `tel:+${digits}`;
}

/** Accessible label for a call link. */
export function callAriaLabel(display: string, who = "Coffman Cove Lodging") {
  return `Call ${who} at ${display}`;
}

/**
 * Normalize any US phone number to the site's canonical display format,
 * `(907) 312-1019`. Partial input is formatted progressively so it can be
 * used for live formatting in form fields. Non-US-length input is returned
 * unchanged so international numbers aren't mangled.
 */
export function formatPhoneDisplay(input: string): string {
  const digits = input.replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (local.length > 10) return input.trim();
  if (local.length === 0) return "";
  if (local.length <= 3) return `(${local}`;
  if (local.length <= 6) return `(${local.slice(0, 3)}) ${local.slice(3)}`;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}


/** Primary lodge email address (bookings + general inquiries). */
export const LODGE_EMAIL = "booking@coffmancovelodging.com";

/** Ready-to-use `mailto:` href for the lodge email. */
export const LODGE_EMAIL_HREF = `mailto:${LODGE_EMAIL}`;
